import { useRef, useState } from 'react';
import SelectOverlay from '__components/Inputs/SelectOverlay';
import { PillDateRangePicker } from './components/PillDateRangePicker';
import { PillDropdownControl } from './components/PillDropdownControl';
import { PillMultiselect } from './components/PillMultiselect';
import { PillSingleselect } from './components/PillSingleselect';
import { getCounts } from './helpers';

type Option<T = string> = { label: string; value: T };

type SingleSelectPillProps = {
  value: string | Option | null;
  title: string;
  placeholder?: string;
  options: Option[];
  onChange(value?: Option): void;
  onClose?(): void;
  onOpen?(onHide: () => void): void;
};

type MultiSelectPillProps<T = string> = {
  value: Option<T>[];
  title: string;
  placeholder?: string;
  options: Option<T>[];
  onChange(value: Option<T>[]): void;
  onClose?(): void;
  onOpen?(onHide: () => void): void;
};

type DateRangePillProps = {
  title: string;
  value: [string, string] | null;
  onChange(value: [string, string]): void;
  onClose?(): void;
  onOpen?(onHide: () => void): void;
};

export const SingleSelectPill = (props: SingleSelectPillProps) => {
  const { options, placeholder, value, title, onChange, onClose, onOpen } =
    props;

  const [isOpen, setIsOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  const handleHide = () => {
    onClose?.();
    setIsOpen(false);
  };

  const handleOpen = () => {
    onOpen?.(handleHide);
    setIsOpen(true);
  };

  return (
    <>
      <PillDropdownControl
        ref={pillRef}
        onClick={handleOpen}
        title={title}
        counts={getCounts(value, 'singleselect')}
        isOpen={isOpen}
      />
      <SelectOverlay
        show={isOpen}
        onHide={handleHide}
        target={pillRef.current}
        iDoNotWantToMountTwice
      >
        <PillSingleselect
          value={value}
          placeholder={placeholder}
          options={options}
          onChange={onChange}
          onHide={handleHide}
        />
      </SelectOverlay>
    </>
  );
};

export const MultiSelectPill = <T,>(props: MultiSelectPillProps<T>) => {
  const { options, placeholder, value, title, onChange, onClose, onOpen } =
    props;

  const [isOpen, setIsOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  const handleHide = () => {
    onClose?.();
    setIsOpen(false);
  };

  const handleOpen = () => {
    onOpen?.(handleHide);
    setIsOpen(true);
  };

  return (
    <>
      <PillDropdownControl
        ref={pillRef}
        onClick={handleOpen}
        title={title}
        counts={getCounts(value, 'multiselect')}
        isOpen={isOpen}
      />
      <SelectOverlay
        show={isOpen}
        onHide={handleHide}
        target={pillRef.current}
        iDoNotWantToMountTwice
      >
        <PillMultiselect
          value={value}
          placeholder={placeholder}
          options={options}
          onChange={onChange}
          onHide={handleHide}
        />
      </SelectOverlay>
    </>
  );
};

export const DateRangePill = (props: DateRangePillProps) => {
  const { value: v, title, onChange, onClose, onOpen } = props;
  const value = v ?? [];

  const [isOpen, setIsOpen] = useState(false);
  const pillRef = useRef<HTMLDivElement>(null);

  const handleHide = () => {
    onClose?.();
    setIsOpen(false);
  };

  const handleOpen = () => {
    onOpen?.(handleHide);
    setIsOpen(true);
  };

  return (
    <>
      <PillDropdownControl
        ref={pillRef}
        onClick={handleOpen}
        counts={getCounts(value, 'date')}
        title={title}
        isOpen={isOpen}
      />
      <SelectOverlay
        show={isOpen}
        onHide={handleHide}
        target={pillRef.current}
        iDoNotWantToMountTwice
      >
        <PillDateRangePicker
          value={value}
          onChange={onChange}
          onHide={handleHide}
        />
      </SelectOverlay>
    </>
  );
};
